import * as React from "react";
import cx from "classnames";

import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import { ErrorText } from "@common/components/error-text";

import "./form-field.styles.scss";

export type FormFieldError = {
  text: string;
};

export enum FormFieldLayout {
  labelThenComponent = "label-then-component",
  componentThenLabel = "component-the-label",
}

export type FieldProps = React.PropsWithChildren<{
  layout?: FormFieldLayout;
  label?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  labelWidth?: number | string;
  description?: React.ReactNode;
  required?: boolean;
  margin?: string | number;
  classes?: {
    label?: string;
    input?: string;
    labelInputWrapper?: string;
    errorText?: string;
  };
  inputWidth?: number | string;
  errors?: Array<FormFieldError>;
  onLabelClick?: () => void;
  id?: string;
}>;

export const FormField: React.FC<FieldProps> = (props) => {
  const {
    onLabelClick,
    layout,
    classes,
    className,
    disabled,
    label,
    children,
    labelWidth,
    required,
    description,
    margin = "12px 0 0 0",
    errors,
    inputWidth,
    id,
  } = props;

  const computedLabelWidth = React.useMemo(() => {
    if (layout === FormFieldLayout.componentThenLabel) {
      return labelWidth;
    }

    return labelWidth ?? 250;
  }, [labelWidth, layout]);

  const computedInputWidth = inputWidth ?? 350;

  return (
    <div className={cx("FormField", className)} style={{ margin }}>
      <Flex
        className={cx("FormField__label-input-wrapper", classes?.labelInputWrapper)}
        alignItems="center"
        justify={layout === FormFieldLayout.componentThenLabel ? "flex-end" : undefined}
        direction={layout === FormFieldLayout.componentThenLabel ? "row-reverse" : undefined}
      >
        {label && (
          <span
            id={id}
            className={cx(
              "FormField__label",
              layout === FormFieldLayout.componentThenLabel
                ? "FormField__label--component-label"
                : "FormField__label--label-component",
              classes?.label
            )}
            style={{ width: computedLabelWidth }}
            onClick={disabled ? undefined : onLabelClick}
          >
            {label} {required && <span className="FormField__required-mark">*</span>}
            {description && <Hint className="FormField__description-mark" description={description} />}
          </span>
        )}
        <div
          className={cx(
            "FormField__input",
            { "FormField__input--disabled": disabled },
            classes?.input
          )}
          style={{ width: computedInputWidth }}
        >
          {children}
        </div>
      </Flex>

      {errors?.map((error, i) => (
        <ErrorText
          withTopGap
          style={{ maxWidth: computedInputWidth }}
          text={error.text}
          key={typeof error.text === "string" ? error.text : i}
          className={classes?.errorText}
        />
      ))}
    </div>
  );
};
